import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { relations, sql } from 'drizzle-orm';
import {
  boolean,
  date,
  doublePrecision,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { softDelete } from '../columns/soft-delete';
import { bataillons } from './bataillons';
import { events } from './events';
import { users } from './users';
export const soldierHowHeardAboutEnum = pgEnum('soldier_how_heard_about', [
  'church',
  'referral',
  'social_media',
  'other',
]);

/** Parentesco do responsável legal — só preenchido para menores de 18. */
export const soldierGuardianRelationshipEnum = pgEnum('soldier_guardian_relationship', [
  'father',
  'mother',
  'grandparent',
  'legal_guardian',
  'other',
]);

/** Patente do recruta no efetivo — a maior é major (marechal/general são só de usuários). */
export const soldierRoleEnum = pgEnum('soldier_role', [
  'major',
  'capitao',
  'tenente',
  'sargento',
  'soldado',
]);
export type SoldierRole = (typeof soldierRoleEnum.enumValues)[number];

export const soldierRoleOptions: Array<{ value: SoldierRole; label: string }> = [
  { value: 'major', label: 'Major' },
  { value: 'capitao', label: 'Capitão' },
  { value: 'tenente', label: 'Tenente' },
  { value: 'sargento', label: 'Sargento' },
  { value: 'soldado', label: 'Soldado' },
];

export const soldiers = pgTable(
  'soldiers',
  {
    id: uuid('id')
      .primaryKey()
      .$defaultFn(() => Bun.randomUUIDv7()),
    name: text('name').notNull(),
    birthday: date('birthday', { mode: 'string' }).notNull(),
    cpf: text('cpf'),
    rg: text('rg'),
    email: text('email'),
    phone: text('phone'),
    photo: text('photo'),
    addressCep: text('address_cep'),
    addressStreet: text('address_street'),
    addressNumber: text('address_number'),
    addressComplement: text('address_complement'),
    addressNeighborhood: text('address_neighborhood').notNull(),
    addressCity: text('address_city').notNull(),
    addressState: text('address_state'),
    emergencyContactName: text('emergency_contact_name'),
    emergencyContactPhone: text('emergency_contact_phone'),
    healthProblems: boolean('health_problems').notNull(),
    healthProblemsDesc: text('health_problems_desc'),
    needRide: boolean('need_ride'),
    paid: boolean('paid'),
    shirtSize: text('shirt_size').notNull(),
    acceptTerms: boolean('accept_terms').notNull(),
    soldierNumber: doublePrecision('soldier_number'),
    role: soldierRoleEnum('role').notNull().default('soldado'),
    /** Check-in no portão (módulo 07): quando recebeu o número e se o pagamento foi recebido ali. */
    checkedInAt: timestamp('checked_in_at'),
    paidAtCheckIn: boolean('paid_at_check_in').notNull().default(false),
    workedInFirstModule: boolean('worked_in_first_module').notNull(),
    workedInSecondModule: boolean('worked_in_second_module').notNull(),
    workedInThirdModule: boolean('worked_in_third_module').notNull(),
    participatedInSecondModule: boolean('participated_in_second_module').notNull(),
    participatedInThirdModule: boolean('participated_in_third_module').notNull(),
    notEnterInContact: boolean('not_enter_in_contact'),
    howHeardAbout: soldierHowHeardAboutEnum('how_heard_about'),
    howHeardAboutOther: text('how_heard_about_other'),
    guardianName: text('guardian_name'),
    guardianCpf: text('guardian_cpf'),
    guardianRelationship: soldierGuardianRelationshipEnum('guardian_relationship'),
    bataillonId: uuid('bataillon_id').references(() => bataillons.id),
    /** Usuário do painel criado para major/capitão — evita duplicar a pessoa. */
    userId: uuid('user_id').references(() => users.id, { onDelete: 'set null' }),
    eventId: uuid('event_id').references(() => events.id),
    isActive: boolean('is_active').default(true),
    createdAt: timestamp('created_at')
      .notNull()
      .$defaultFn(() => new Date()),
    updatedAt: timestamp('updated_at')
      .$onUpdate(() => new Date())
      .notNull(),
    ...softDelete,
  },
  (table) => [
    uniqueIndex('soldiers_user_id_idx')
      .on(table.userId)
      .where(sql`${table.userId} is not null`),
  ],
);

export const soldiersRelations = relations(soldiers, ({ one }) => ({
  user: one(users, {
    fields: [soldiers.userId],
    references: [users.id],
  }),
  bataillon: one(bataillons, {
    fields: [soldiers.bataillonId],
    references: [bataillons.id],
  }),
  event: one(events, {
    fields: [soldiers.eventId],
    references: [events.id],
  }),
}));

export type Soldier = InferSelectModel<typeof soldiers>;
export type CreateSoldierInput = InferInsertModel<typeof soldiers>;
export type UpdateSoldierInput = Partial<CreateSoldierInput>;
